import { ApolloError } from '@apollo/client';

import { STATUS_CODES } from '@/constants/common';
import { useSnackbar } from '@/providers/SnackbarProvider';

export const getGraphQLErrorCode = (error: ApolloError): string | undefined =>
  error.graphQLErrors[0]?.extensions?.code as string | undefined;

type PayloadError = {
  message?: string;
};

interface HandleGraphQLErrorsOptions {
  error: ApolloError;
  customMessage?: string;
  onLockout?: (lockoutEnd: number) => void;
  statusMessages?: Record<string, string>;
  onErrorMessage?: (message: string) => void;
}

export const useGraphQLErrorProcessor = () => {
  const { openSnackbar } = useSnackbar();

  return ({
    error,
    onLockout,
    customMessage,
    statusMessages = {},
    onErrorMessage
  }: HandleGraphQLErrorsOptions) => {
    if (!error.graphQLErrors.length) return;

    for (const { extensions, message } of error.graphQLErrors) {
      const { code, lockExpiresAt, payloadErrors } = (extensions || {}) as {
        code?: string;
        lockExpiresAt?: number;
        payloadErrors?: PayloadError[];
      };

      const payloadErrorMessage = payloadErrors?.[0]?.message;

      const errorMessage =
        payloadErrorMessage ||
        statusMessages[code as string] ||
        customMessage ||
        message;

      switch (code) {
        case STATUS_CODES.LIMIT_EXCEEDED:
          if (lockExpiresAt && onLockout) {
            return onLockout(lockExpiresAt);
          }

          if (onErrorMessage) {
            onErrorMessage(errorMessage);
          } else {
            openSnackbar(errorMessage, 'error');
          }
          break;

        case STATUS_CODES.AUTHENTICATION_ERROR:
          throw new Error(errorMessage);

        default:
          if (onErrorMessage) {
            onErrorMessage(errorMessage);
          } else {
            openSnackbar(errorMessage, 'error');
          }
          break;
      }
    }
  };
};
